import { file as sdkFile } from 'sharetribe-flex-sdk';

export const MAX_MESSAGE_FILES = 5;
export const MAX_MESSAGE_FILE_BYTES = 20 * 1024 * 1024;
const MIME_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};
export const MESSAGE_FILE_ACCEPT = Object.keys(MIME_TYPES)
  .map(ext => `.${ext}`)
  .join(',');

/** Local convenience limits; Sharetribe independently verifies MIME, size and malware. */
export const validateMessageFile = file => {
  const extension = file?.name
    ?.split('.')
    .pop()
    .toLowerCase();
  if (!MIME_TYPES[extension]) return 'type';
  if (!file.size || file.size > MAX_MESSAGE_FILE_BYTES) return 'size';
  if (
    file.name.length > 255 ||
    /[\x00-\x1f\x7f/\\]/.test(file.name) ||
    file.name !== file.name.trim()
  )
    return 'name';
  return null;
};

/** Reject non-HTTPS and credential-bearing URLs returned by a download/upload response. */
export const safeFileUrl = value => {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password)
    throw new Error('unsafe-file-url');
  return url.href;
};

/** Upload once and wait for the provider's verification; never attach an unchecked file. */
export const uploadMessageFile = async (
  sdk,
  file,
  {
    onStatus = () => {},
    signal,
    wait = ms => new Promise(resolve => setTimeout(resolve, ms)),
  } = {}
) => {
  const invalid = validateMessageFile(file);
  if (invalid) throw new Error(invalid);
  const checkCancelled = () => {
    if (signal?.aborted) throw new Error('cancelled');
  };
  checkCancelled();
  const createdAt = Date.now();
  onStatus('uploading');
  const created = await sdk.ownFiles.create({
    name: file.name,
    size: file.size,
    mimeType:
      MIME_TYPES[
        file.name
          .split('.')
          .pop()
          .toLowerCase()
      ],
  });
  const id = created.data.data.id;
  checkCancelled();
  const upload = await sdk.fileUploads.create({ fileId: id });
  const { url, method, headers } = upload.data.data.attributes;
  checkCancelled();
  await sdkFile.upload({ url: safeFileUrl(url), method, headers, file });
  onStatus('verifying');
  for (let attempt = 0; attempt < 30; attempt++) {
    checkCancelled();
    await wait(attempt === 0 ? 1000 : 2000);
    checkCancelled();
    const response = await sdk.ownFiles.show({ id });
    const entity = response.data.data;
    if (entity.attributes.state === 'available')
      return { id, name: file.name, size: file.size, createdAt };
    if (
      entity.attributes.state === 'verificationFailed' ||
      entity.attributes.deleted
    )
      throw new Error('verification');
  }
  throw new Error('timeout');
};

/** Fresh authorized URL on each click; no permanent download links in application state. */
export const downloadMessageFile = async (sdk, fileAttachmentId) => {
  const response = await sdk.fileDownloads.create({ fileAttachmentId });
  return safeFileUrl(response.data.data.attributes.url);
};
